import React, { useState } from 'react';
import { Button } from '../../../ui/button';
import { IconCircleDashed, IconUsers, IconRocket, IconCurrencyDollar, IconCheck, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { useDrop } from 'react-dnd';
import CampaignCard from './CampaignCard';
import { DND_TYPES, isValidTransition } from './workflowConstants';
import { useWorkflow } from '../../../../contexts/WorkflowContext';
import { cn } from '../../../../lib/utils';

const STAGE_CONFIG = {
 waiting_to_start: { title: 'Waiting to Start', compactTitle: 'Waiting', icon: IconCircleDashed, stageKey: 'waiting' },
 recruitment: { title: 'Recruitment', icon: IconUsers, stageKey: 'recruitment' },
 live: { title: 'Live', icon: IconRocket, stageKey: 'live' },
 payment_report: { title: 'Payments & Report', icon: IconCurrencyDollar, stageKey: 'payment' },
 finished: { title: 'Finished', icon: IconCheck, stageKey: 'finished' },
};

const STAGE_STYLES = {
 waiting: { bg: 'bg-surface-2', border: 'border-surface-2', text: 'text-text-tertiary', badge: 'bg-surface-3', icon: 'text-text-tertiary' },
 recruitment: { bg: 'bg-accent-muted', border: 'border-transparent', text: 'text-accent', badge: 'bg-accent-muted', icon: 'text-accent' },
 live: { bg: 'bg-live-muted', border: 'border-transparent', text: 'text-live', badge: 'bg-live-muted', icon: 'text-live' },
 payment: { bg: 'bg-warning-muted', border: 'border-transparent', text: 'text-warning', badge: 'bg-warning-muted', icon: 'text-warning' },
 finished: { bg: 'bg-surface-2', border: 'border-surface-2', text: 'text-text-tertiary', badge: 'bg-surface-3', icon: 'text-text-tertiary' },
};

const StageColumn = ({ stage, campaigns, count, onCampaignClick, onCampaignSettings, canManageCampaigns = true }) => {
 const { transitionCampaign } = useWorkflow();
 const canCollapse = stage === 'waiting_to_start' || stage === 'finished';
 const [isExpanded, setIsExpanded] = useState(!canCollapse);

 const config = STAGE_CONFIG[stage];
 const styles = STAGE_STYLES[config.stageKey];
 const IconComponent = config.icon;

 const [{ isOver, canDrop }, dropRef] = useDrop({
 accept: DND_TYPES.CAMPAIGN_CARD,
 drop: async (item) => {
 if (!canManageCampaigns) return { targetStage: stage, success: false };
 try {
 await transitionCampaign(item.campaignId, stage, 'Drag-and-drop transition');
 return { targetStage: stage, success: true };
 } catch (error) {
 console.error('[StageColumn] Transition failed:', error);
 return { targetStage: stage, success: false, error };
 }
 },
 canDrop: (item) => canManageCampaigns && isValidTransition(item.currentStage, stage),
 collect: (monitor) => ({
 isOver: monitor.isOver(),
 canDrop: monitor.canDrop()
 })
 });

 const isValidDrop = isOver && canDrop;
 const isInvalidDrop = isOver && !canDrop;
 const isCollapsed = canCollapse && !isExpanded;

 return (
 <div
 ref={dropRef}
 role="region"
 aria-label={`${config.title} campaigns`}
 className={cn(
 'flex flex-col min-h-0 border-2',
 isValidDrop && 'border-accent bg-accent-muted',
 isInvalidDrop && 'border-error bg-error-muted',
 !isValidDrop && !isInvalidDrop && 'border-transparent',
 isCollapsed ? 'w-32 flex-none self-start' : 'h-full flex-1 min-w-[224px]',
 )}
 >
 {/* Stage Header */}
 <div
 className={cn(
 'flex-shrink-0 border w-full',
 styles.bg, styles.border,
 isCollapsed ? 'px-1.5 py-3' : 'p-3 mb-4',
 )}
 >
 <div className={cn('flex items-center min-h-11', isCollapsed ? 'gap-1' : 'gap-2')}>
 {/* Icon */}
 {!isCollapsed && <div className={cn('flex flex-shrink-0 items-center justify-center w-7 h-7', styles.badge)}>
 <IconComponent size={20} strokeWidth={2} className={styles.icon} />
 </div>}

 {/* Title */}
 <h6 aria-label={config.title} title={config.title} className={cn('font-semibold -tracking-tight flex-1 min-w-0', isCollapsed ? 'text-sm truncate' : 'text-[15px]', styles.text)}>
 {isCollapsed ? config.compactTitle || config.title : config.title}
 </h6>

 {/* Badge */}
 <div className={cn(
 'inline-flex flex-shrink-0 items-center justify-center font-semibold tabular-nums text-sm h-6 min-w-[24px] px-1',
 styles.badge, styles.text
 )}>
 {count}
 </div>

 {/* Collapse toggle */}
 {canCollapse && (
 <Button type="button" variant="ghost" size="icon" aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${config.title}`} aria-expanded={isExpanded} aria-controls={`stage-cards-${stage}`} onClick={() => setIsExpanded((expanded) => !expanded)} className={cn('h-11 flex-shrink-0', isCollapsed ? 'w-6' : 'w-11', styles.text)}>
 {isExpanded ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
 </Button>
 )}
 </div>
 </div>

 {/* Cards */}
 <div id={`stage-cards-${stage}`} hidden={!isExpanded} className={isExpanded ? 'flex-1 flex flex-col overflow-hidden w-full min-h-0' : 'hidden'}>
 {isExpanded && <div className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col" style={{ minHeight: 0 }}>
 {campaigns.length === 0 ? (
 <div className="flex-shrink-0 text-center bg-surface-1/50 p-8">
 <IconComponent size={32} strokeWidth={1.5} className="text-text-tertiary/40 mb-3 mx-auto" />
 <p className="text-sm font-medium text-text-tertiary">No campaigns in this stage</p>
 </div>
 ) : (
 campaigns.map((campaign, index) => (
 <div
 key={campaign._id}
 className="flex-shrink-0"
 style={{ marginBottom: index < campaigns.length - 1 ? 12 : 0 }}
 >
 <CampaignCard
 campaign={campaign}
 stage={stage}
 onClick={onCampaignClick}
 onSettingsClick={onCampaignSettings}
 canManageCampaigns={canManageCampaigns}
 />
 </div>
 ))
 )}
 </div>}
 </div>
 </div>
 );
};

export default React.memo(StageColumn);
